import { useMemo } from 'react';
import type { CouncilMember, DropdownOptions, AttendanceSummaryItem } from '../../api';
import { capitalizeFirstLetter, useFormatters } from '../../utils/texts';
import { useAttendanceColors } from '../../utils/hooks';
import ContentSection from '../common/ContentSection/ContentSection';
import VoteBar from '../common/VoteBar/VoteBar';
import { useTranslation } from 'react-i18next';

type Props = {
    selectedAttendanceState: {
        value: string | null;
        setValue: (value: string | null) => void;
    };
    memberData: CouncilMember;
    selectedParty: DropdownOptions | null;
    attendanceLabels: Record<string, string>;
};

const MemberDetailAttendanceSummaryChartSection = ({ selectedAttendanceState, memberData, selectedParty, attendanceLabels }: Props) => {
    const { t, i18n } = useTranslation('members');
    const { value: selectedAttendance, setValue: setSelectedAttendance } = selectedAttendanceState;
    const attendanceColors: { [key: string]: string } = useAttendanceColors();
    const currentLocale = i18n.language;
    const formatter = useFormatters(currentLocale);

    const dataSource: AttendanceSummaryItem[] = useMemo(() => {
        if (!selectedParty || selectedParty.id === 0) {
            return memberData.dochazka?.sumarizace ?? [];
        }

        return memberData.votes_per_party?.find((item) => item.party_id === selectedParty.id)?.attendance_sum ?? [];
    }, [memberData, selectedParty]);

    const getLabel = (item: AttendanceSummaryItem) => capitalizeFirstLetter(attendanceLabels[item.id] ?? item.code);

    const handleSegmentClick = (item: AttendanceSummaryItem) => {
        setSelectedAttendance(item.code !== selectedAttendance ? item.code : null);
    };

    return (
        <ContentSection
            title={t('memberAttendance')}
            subtitle={t('tooltipChart', { ns: 'translation' })}
            className='dashboard-card'
        >
            <VoteBar
                items={dataSource}
                colors={attendanceColors}
                getLabel={getLabel}
                formatPercent={formatter.percentFrom100}
                onSegmentClick={handleSegmentClick}
            />
        </ContentSection>
    );
};

export default MemberDetailAttendanceSummaryChartSection;
